import React, { useState, useEffect } from 'react';
import StandingsTable from './StandingsTable';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import { getStandings } from '../../services/public-api';
import './DivisionStandings.css';

/**
 * DivisionStandings component
 * Displays standings filtered by division with division selector
 * T066: Division-filtered standings display
 */
const DivisionStandings = ({ competitionId, divisions = [] }) => {
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Normalize divisions - handle both string[] and object[] formats
  const normalizedDivisions = divisions.map((div, index) => {
    if (typeof div === 'string') {
      return { divisionId: div, name: div };
    }
    return div;
  });

  useEffect(() => {
    // Set first division as default
    if (normalizedDivisions.length > 0 && !selectedDivision) {
      setSelectedDivision(normalizedDivisions[0].divisionId);
    }
  }, [divisions, selectedDivision]);

  useEffect(() => {
    if (selectedDivision) {
      fetchStandings();
    }
  }, [selectedDivision]);

  const fetchStandings = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('Fetching standings:', { competitionId, selectedDivision });
      const data = await getStandings(competitionId, selectedDivision);
      console.log('Standings data received:', data);
      setStandings(data.standings || []);
    } catch (err) {
      console.error('Error fetching standings:', err);
      setError(err.message || 'Failed to load standings');
    } finally {
      setLoading(false);
    }
  };

  if (divisions.length === 0) {
    return (
      <div className="division-standings-empty">
        <p>No divisions configured for this competition.</p>
      </div>
    );
  }

  const handleDivisionChange = (divisionId) => {
    setSelectedDivision(divisionId);
  };

  const selectedDivisionName = normalizedDivisions.find(
    (d) => d.divisionId === selectedDivision
  )?.name || 'Division';

  return (
    <div className="division-standings">
      <div className="division-selector">
        <label htmlFor="division-select" className="division-label">
          Select Division:
        </label>
        <div className="division-tabs">
          {normalizedDivisions.map((division) => (
            <button
              key={division.divisionId}
              onClick={() => handleDivisionChange(division.divisionId)}
              className={`division-tab ${
                selectedDivision === division.divisionId ? 'active' : ''
              }`}
              aria-pressed={selectedDivision === division.divisionId}
            >
              {division.name}
            </button>
          ))}
        </div>
      </div>

      <div className="standings-content">
        <h3 className="division-title">{selectedDivisionName} Standings</h3>

        {loading && <Loading size="medium" />}

        {error && (
          <ErrorMessage
            message={error}
            variant="error"
            onRetry={fetchStandings}
          />
        )}

        {!loading && !error && <StandingsTable standings={standings} />}
      </div>
    </div>
  );
};

export default DivisionStandings;
